import type { File } from "~/models/polyschema/File"
import type { SearchIndexingStatus } from "~/models/polyschema/SearchIndexingStatus"

import { FileAudioContainerMetadataBuf, FileBuf, FileIdBuf, FileLinkOpenGraphMetadataBuf, FilePdfContainerMetadataBuf, FilePropertyBuf, FilePropertyIdBuf, FileThumbnailBuf, FileThumbnailSetBuf, FileVideoContainerMetadataBuf, SearchIndexingStatusBuf } from "~/classes/generated/file"

import { maybe } from "../helpers"
import { parseVector } from "./datagram"
import { addEntityPropertySelectionToBuf, addEntityPropertyTimestampToBuf, addEntityPropertyValueToBuf, asEntityPropertyStatusBuf, parseEntityPropertySchemaValue, parseEntityPropertySelection, parseEntityPropertyStatus, parseEntityPropertyTimestampBuf } from "./entity"
import { addUserIdToBuf, addUuidToBuf, parseFileId, parseFilePropertyId, parseUserId } from "./id"

// File operations

export function parseFileProperty(buf: FilePropertyBuf) {
  // For some unknown reason (as of 11-7-2024), when using tauri channels, there is an
  // odd bug where the `valueArray` return null. While we figure that out, here is a kludge
  let value = buf.valueArray()
  if (!value?.length)
    value = new Uint8Array(parseVector(x => buf.value(x)!, buf.valueLength()))
  const fileProperty: FileProperty = {
    id: parseFileId(buf.id()!),
    propertyId: parseFilePropertyId(buf.propertyId()!),
    version: buf.version(),
    schema: buf.schema(),
    value: parseEntityPropertySchemaValue(buf.schema(), value),
    timestamp: parseEntityPropertyTimestampBuf(buf.timestamp()!),
    deleted: buf.deleted(),
    status: parseEntityPropertyStatus(buf.status()),
    epochId: buf.epochId() ?? undefined,
    sharedWithUserId: maybe(parseUserId)(buf.sharedWithUserId()),
    author: parseUserId(buf.author()!),
  }
  return fileProperty
}

export function addFileIdToBuf(builder: DatagramBufferBuilder, entityId: string) {
  const [api, key] = partition(entityId, "-")
  const apiId = Number.parseInt(api, 16)
  const keyId = addUuidToBuf(builder, key)
  FileIdBuf.startFileIdBuf(builder)
  FileIdBuf.addApiId(builder, apiId)
  FileIdBuf.addKeyId(builder, keyId)
  return FileIdBuf.endFileIdBuf(builder)
}

export function addFilePropertyIdToBuf(builder: DatagramBufferBuilder, propertyId: string) {
  const [groupId, keyId] = partition(propertyId, "-")
  const group = Number.parseInt(groupId, 16)
  const key = addUuidToBuf(builder, keyId)
  FilePropertyIdBuf.startFilePropertyIdBuf(builder)
  FilePropertyIdBuf.addGroup(builder, group)
  FilePropertyIdBuf.addKey(builder, key)
  return FilePropertyIdBuf.endFilePropertyIdBuf(builder)
}

export function addFilePropertyToBuf(builder: DatagramBufferBuilder, property: FileProperty) {
  const value = addEntityPropertyValueToBuf(builder, property.value)
  const fileId = addFileIdToBuf(builder, property.id)
  const propertyId = addFilePropertyIdToBuf(builder, property.propertyId)
  const timestamp = addEntityPropertyTimestampToBuf(builder, property.timestamp)
  const author = addUserIdToBuf(builder, property.author)
  let sharedWithUserId
  if (property.sharedWithUserId)
    sharedWithUserId = addUserIdToBuf(builder, property.sharedWithUserId)

  FilePropertyBuf.startFilePropertyBuf(builder)
  FilePropertyBuf.addId(builder, fileId)
  FilePropertyBuf.addPropertyId(builder, propertyId)
  FilePropertyBuf.addVersion(builder, property.version)
  FilePropertyBuf.addSchema(builder, property.schema)
  FilePropertyBuf.addValue(builder, value)
  FilePropertyBuf.addTimestamp(builder, timestamp)
  if (property.deleted)
    FilePropertyBuf.addDeleted(builder, property.deleted)
  FilePropertyBuf.addStatus(builder, asEntityPropertyStatusBuf(property.status))
  if (property.epochId !== undefined)
    FilePropertyBuf.addEpochId(builder, property.epochId)
  if (sharedWithUserId)
    FilePropertyBuf.addSharedWithUserId(builder, sharedWithUserId)
  FilePropertyBuf.addAuthor(builder, author)
  return FilePropertyBuf.endFilePropertyBuf(builder)
}

export function parseFile(buf: FileBuf): File {
  const id = parseFileId(buf.id()!)
  const selection = parseEntityPropertySelection(buf.selection())
  const properties: Record<string, FileProperty> = {}
  for (let i = 0; i < buf.propertiesLength(); i++) {
    const property = parseFileProperty(buf.properties(i)!)
    properties[property.propertyId] = property
  }
  let path: File[] | undefined
  if (buf.containsPath()) {
    path = []
    for (let i = 0; i < buf.pathLength(); i++) {
      path.push(parseFile(buf.path(i)!))
    }
  }
  return {
    id,
    selection,
    properties,
    path,
  }
}

export function addFilePathToBuf(builder: DatagramBufferBuilder, path: FilePath) {
  return Object.values(path).map(f => addFileToBuf(builder, f))
}

export function addFileToBuf(builder: DatagramBufferBuilder, file: File) {
  const innerProperties = Object.values(file.properties).map(p => addFilePropertyToBuf(builder, p!))
  const properties = FileBuf.createPropertiesVector(builder, innerProperties)
  let selection
  if (file.selection)
    selection = addEntityPropertySelectionToBuf(builder, file.selection)
  let path
  if (isDefined(file.path))
    path = FileBuf.createPathVector(builder, addFilePathToBuf(builder, file.path))
  const id = addFileIdToBuf(builder, file.id)
  FileBuf.startFileBuf(builder)
  FileBuf.addId(builder, id)
  FileBuf.addProperties(builder, properties)
  if (selection)
    FileBuf.addSelection(builder, selection)
  if (path) {
    FileBuf.addPath(builder, path)
    FileBuf.addContainsPath(builder, true)
  }
  return FileBuf.endFileBuf(builder)
}

export function parseFileThumbnail(buf: FileThumbnailBuf): FileThumbnail {
  return {
    kind: buf.kind(),
    width: Number(buf.width()),
    height: Number(buf.height()),
    uri: buf.uri()!,
  }
}

export function addFileThumbnailToBuf(builder: DatagramBufferBuilder, thumbnail: FileThumbnail) {
  const uri = builder.createString(thumbnail.uri)
  FileThumbnailBuf.startFileThumbnailBuf(builder)
  FileThumbnailBuf.addKind(builder, thumbnail.kind)
  FileThumbnailBuf.addHeight(builder, BigInt(thumbnail.height))
  FileThumbnailBuf.addWidth(builder, BigInt(thumbnail.width))
  FileThumbnailBuf.addUri(builder, uri)
  return FileThumbnailBuf.endFileThumbnailBuf(builder)
}

export function parseFileThumbnailSet(buf: FileThumbnailSetBuf): FileThumbnailSet {
  const thumbnails = parseVector(n => buf.images(n)!, buf.imagesLength())
    .map(buf => parseFileThumbnail(buf))
  const images = dictByKey(thumbnails, t => t.kind)
  return { images }
}

export function addFileThumbnailSetToBuf(builder: DatagramBufferBuilder, set: FileThumbnailSet) {
  const thumbnailVector = Object.values(set.images).map(t => addFileThumbnailToBuf(builder, t))
  const images = FileThumbnailSetBuf.createImagesVector(builder, thumbnailVector)
  FileThumbnailSetBuf.startFileThumbnailSetBuf(builder)
  FileThumbnailSetBuf.addImages(builder, images)
  return FileThumbnailSetBuf.endFileThumbnailSetBuf(builder)
}

export function parseFileAudioContainerMetadata(buf: FileAudioContainerMetadataBuf): FileAudioContainerMetadata {
  return {
    album: buf.album() ?? undefined,
    albumArtist: buf.albumArtist() ?? undefined,
    artist: buf.artist() ?? undefined,
    comment: buf.comment() ?? undefined,
    composer: buf.composer() ?? undefined,
    copyright: buf.copyright() ?? undefined,
    encoder: buf.encoder() ?? undefined,
    encodedBy: buf.encodedBy() ?? undefined,
    genre: buf.genre() ?? undefined,
    language: buf.language() ?? undefined,
    performer: buf.performer() ?? undefined,
    disc: buf.disc() ? Number(buf.disc()!) : undefined,
    title: buf.title() ?? undefined,
    date: buf.date() ?? undefined,
    lyrics: buf.lyrics() ?? undefined,
    rating: buf.rating() ? Number(buf.rating()!) : undefined,
    urlSource: buf.urlSource() ?? undefined,
  }
}

export function addFileAudioContainerMetadataToBuf(
  builder: DatagramBufferBuilder,
  metadata: FileAudioContainerMetadata,
) {
  let album
  if (metadata.album)
    album = builder.createString(metadata.album)
  let albumArtist
  if (metadata.albumArtist)
    albumArtist = builder.createString(metadata.albumArtist)
  let artist
  if (metadata.artist)
    artist = builder.createString(metadata.artist)
  let beatsPerMinute
  if (metadata.beatsPerMinute)
    beatsPerMinute = metadata.beatsPerMinute
  let comment
  if (metadata.comment)
    comment = builder.createString(metadata.comment)
  let composer
  if (metadata.composer)
    composer = builder.createString(metadata.composer)
  let copyright
  if (metadata.copyright)
    copyright = builder.createString(metadata.copyright)
  let encoder
  if (metadata.encoder)
    encoder = builder.createString(metadata.encoder)
  let encodedBy
  if (metadata.encodedBy)
    encodedBy = builder.createString(metadata.encodedBy)
  let genre
  if (metadata.genre)
    genre = builder.createString(metadata.genre)
  let language
  if (metadata.language)
    language = builder.createString(metadata.language)
  let performer
  if (metadata.performer)
    performer = builder.createString(metadata.performer)
  let disc
  if (metadata.disc)
    disc = metadata.disc
  let title
  if (metadata.title)
    title = builder.createString(metadata.title)
  let trackNumber
  if (metadata.trackNumber)
    trackNumber = metadata.trackNumber
  let date
  if (metadata.date)
    date = builder.createString(metadata.date)
  let urlSource
  if (metadata.urlSource)
    urlSource = builder.createString(metadata.urlSource)
  FileAudioContainerMetadataBuf.startFileAudioContainerMetadataBuf(builder)
  if (album)
    FileAudioContainerMetadataBuf.addAlbum(builder, album)
  if (albumArtist)
    FileAudioContainerMetadataBuf.addAlbumArtist(builder, albumArtist)
  if (artist)
    FileAudioContainerMetadataBuf.addArtist(builder, artist)
  if (beatsPerMinute)
    FileAudioContainerMetadataBuf.addBeatsPerMinute(builder, beatsPerMinute)
  if (comment)
    FileAudioContainerMetadataBuf.addComment(builder, comment)
  if (composer)
    FileAudioContainerMetadataBuf.addComposer(builder, composer)
  if (copyright)
    FileAudioContainerMetadataBuf.addCopyright(builder, copyright)
  if (encoder)
    FileAudioContainerMetadataBuf.addEncoder(builder, encoder)
  if (encodedBy)
    FileAudioContainerMetadataBuf.addEncodedBy(builder, encodedBy)
  if (genre)
    FileAudioContainerMetadataBuf.addGenre(builder, genre)
  if (language)
    FileAudioContainerMetadataBuf.addLanguage(builder, language)
  if (performer)
    FileAudioContainerMetadataBuf.addPerformer(builder, performer)
  if (disc)
    FileAudioContainerMetadataBuf.addDisc(builder, BigInt(disc))
  if (title)
    FileAudioContainerMetadataBuf.addTitle(builder, title)
  if (trackNumber)
    FileAudioContainerMetadataBuf.addTrackNumber(builder, BigInt(trackNumber))
  if (date)
    FileAudioContainerMetadataBuf.addDate(builder, date)
  if (urlSource)
    FileAudioContainerMetadataBuf.addUrlSource(builder, urlSource)
  return FileAudioContainerMetadataBuf.endFileAudioContainerMetadataBuf(builder)
}
export function parseFileVideoContainerMetadata(
  buf: FileVideoContainerMetadataBuf,
): FileVideoContainerMetadata {
  return {
    pixelFormat: buf.pixelFormat() ?? undefined,
    frameRate: buf.frameRate() ?? undefined,
    bitRate: buf.bitRate() === null ? undefined : Number(buf.bitRate()),
    colorSpace: buf.colorSpace() ?? undefined,
    colorRange: buf.colorRange() ?? undefined,
    colorPrimaries: buf.colorPrimaries() ?? undefined,
    colorTransferCharacteristic: buf.colorTransferCharacteristic() ?? undefined,
    chromaLocation: buf.chromaLocation() ?? undefined,
    aspectRatio: buf.aspectRatio() ?? undefined,
    codecName: buf.codecName() ?? undefined,
    codecDescription: buf.codecDescription() ?? undefined,
  }
}

export function addFileVideoContainerMetadataToBuf(
  builder: DatagramBufferBuilder,
  metadata: FileVideoContainerMetadata,
) {
  let pixelFormat
  if (metadata.pixelFormat)
    pixelFormat = builder.createString(metadata.pixelFormat)
  let frameRate
  if (metadata.frameRate)
    frameRate = metadata.frameRate
  let bitRate
  if (metadata.bitRate)
    bitRate = metadata.bitRate
  let colorSpace
  if (metadata.colorSpace)
    colorSpace = builder.createString(metadata.colorSpace)
  let colorRange
  if (metadata.colorRange)
    colorRange = builder.createString(metadata.colorRange)
  let colorPrimaries
  if (metadata.colorPrimaries)
    colorPrimaries = builder.createString(metadata.colorPrimaries)
  let colorTransferCharacteristic
  if (metadata.colorTransferCharacteristic)
    colorTransferCharacteristic = builder.createString(metadata.colorTransferCharacteristic)
  let chromaLocation
  if (metadata.chromaLocation)
    chromaLocation = builder.createString(metadata.chromaLocation)
  let aspectRatio
  if (metadata.aspectRatio)
    aspectRatio = builder.createString(metadata.aspectRatio)
  let codecName
  if (metadata.codecName)
    codecName = builder.createString(metadata.codecName)
  let codecDescription
  if (metadata.codecDescription)
    codecDescription = builder.createString(metadata.codecDescription)

  FileVideoContainerMetadataBuf.startFileVideoContainerMetadataBuf(builder)
  if (pixelFormat)
    FileVideoContainerMetadataBuf.addPixelFormat(builder, pixelFormat)
  if (frameRate)
    FileVideoContainerMetadataBuf.addFrameRate(builder, frameRate)
  if (bitRate)
    FileVideoContainerMetadataBuf.addBitRate(builder, BigInt(bitRate))
  if (colorSpace)
    FileVideoContainerMetadataBuf.addColorSpace(builder, colorSpace)
  if (colorRange)
    FileVideoContainerMetadataBuf.addColorRange(builder, colorRange)
  if (colorPrimaries)
    FileVideoContainerMetadataBuf.addColorPrimaries(builder, colorPrimaries)
  if (colorTransferCharacteristic)
    FileVideoContainerMetadataBuf.addColorTransferCharacteristic(builder, colorTransferCharacteristic)
  if (chromaLocation)
    FileVideoContainerMetadataBuf.addChromaLocation(builder, chromaLocation)
  if (aspectRatio)
    FileVideoContainerMetadataBuf.addAspectRatio(builder, aspectRatio)
  if (codecName)
    FileVideoContainerMetadataBuf.addCodecName(builder, codecName)
  if (codecDescription)
    FileVideoContainerMetadataBuf.addCodecDescription(builder, codecDescription)
  return FileVideoContainerMetadataBuf.endFileVideoContainerMetadataBuf(builder)
}
export function parseFilePdfContainerMetadata(
  buf: FilePdfContainerMetadataBuf,
): FilePdfContainerMetadata {
  return {
    format: buf.format() || undefined,
    encryption: buf.encryption() || undefined,
    author: buf.author() || undefined,
    title: buf.title() || undefined,
    producer: buf.producer() || undefined,
    creator: buf.creator() || undefined,
    subject: buf.subject() || undefined,
    keywords: buf.keywords() || undefined,
  }
}

export function addFilePdfContainerMetadataToBuf(
  builder: DatagramBufferBuilder,
  metadata: FilePdfContainerMetadata,
) {
  let format
  if (metadata.format)
    format = builder.createString(metadata.format)
  let encryption
  if (metadata.encryption)
    encryption = builder.createString(metadata.encryption)
  let author
  if (metadata.author)
    author = builder.createString(metadata.author)
  let title
  if (metadata.title)
    title = builder.createString(metadata.title)
  let producer
  if (metadata.producer)
    producer = builder.createString(metadata.producer)
  let creator
  if (metadata.creator)
    creator = builder.createString(metadata.creator)
  let subject
  if (metadata.subject)
    subject = builder.createString(metadata.subject)
  let keywords
  if (metadata.keywords)
    keywords = builder.createString(metadata.keywords)
  FilePdfContainerMetadataBuf.startFilePdfContainerMetadataBuf(builder)
  if (format)
    FilePdfContainerMetadataBuf.addFormat(builder, format)
  if (encryption)
    FilePdfContainerMetadataBuf.addEncryption(builder, encryption)
  if (author)
    FilePdfContainerMetadataBuf.addAuthor(builder, author)
  if (title)
    FilePdfContainerMetadataBuf.addTitle(builder, title)
  if (producer)
    FilePdfContainerMetadataBuf.addProducer(builder, producer)
  if (creator)
    FilePdfContainerMetadataBuf.addCreator(builder, creator)
  if (subject)
    FilePdfContainerMetadataBuf.addSubject(builder, subject)
  if (keywords)
    FilePdfContainerMetadataBuf.addKeywords(builder, keywords)
  return FilePdfContainerMetadataBuf.endFilePdfContainerMetadataBuf(builder)
}
export function parseFileLinkOpenGraphMetadata(
  buf: FileLinkOpenGraphMetadataBuf,
): FileLinkOpenGraphMetadata {
  return {
    title: buf.title() ?? undefined,
    description: buf.description() ?? undefined,
    favicon: buf.favicon() ?? undefined,
    image: buf.image() ?? undefined,
    contentType: buf.contentType() ?? undefined,
  }
}

export function addFileLinkOpenGraphMetadataToBuf(
  builder: DatagramBufferBuilder,
  metadata: FileLinkOpenGraphMetadata,
) {
  let title
  if (metadata.title)
    title = builder.createString(title)
  let description
  if (metadata.description)
    description = builder.createString(description)
  let favicon
  if (metadata.favicon)
    favicon = builder.createString(favicon)
  let image
  if (metadata.image)
    image = builder.createString(image)
  let contentType
  if (metadata.contentType)
    contentType = builder.createString(contentType)
  FileLinkOpenGraphMetadataBuf.startFileLinkOpenGraphMetadataBuf(builder)
  if (title)
    FileLinkOpenGraphMetadataBuf.addTitle(builder, title)
  if (description)
    FileLinkOpenGraphMetadataBuf.addDescription(builder, description)
  if (favicon)
    FileLinkOpenGraphMetadataBuf.addFavicon(builder, favicon)
  if (image)
    FileLinkOpenGraphMetadataBuf.addImage(builder, image)
  if (contentType)
    FileLinkOpenGraphMetadataBuf.addContentType(builder, contentType)
  return FileLinkOpenGraphMetadataBuf.endFileLinkOpenGraphMetadataBuf(builder)
}

export function parseSearchIndexingStatus(buf: SearchIndexingStatusBuf): SearchIndexingStatus {
  return {
    loadStatus: buf.loadStatus(),
    metadataExtractionStatus: buf.metadataExtractionStatus(),
    metadataIndexingStatus: buf.metadataIndexingStatus(),
    thumbnailStatus: buf.thumbnailStatus(),
    transcodeStatus: buf.transcodeStatus(),
    embeddingsStatus: buf.embeddingsStatus(),
  }
}

export function addSearchIndexingStatusToBuf(builder: DatagramBufferBuilder, status: SearchIndexingStatus) {
  SearchIndexingStatusBuf.startSearchIndexingStatusBuf(builder)
  SearchIndexingStatusBuf.addLoadStatus(builder, status.loadStatus)
  SearchIndexingStatusBuf.addMetadataExtractionStatus(builder, status.metadataExtractionStatus)
  SearchIndexingStatusBuf.addMetadataIndexingStatus(builder, status.metadataIndexingStatus)
  SearchIndexingStatusBuf.addThumbnailStatus(builder, status.thumbnailStatus)
  SearchIndexingStatusBuf.addTranscodeStatus(builder, status.transcodeStatus)
  SearchIndexingStatusBuf.addEmbeddingsStatus(builder, status.embeddingsStatus)
  return SearchIndexingStatusBuf.endSearchIndexingStatusBuf(builder)
}
